import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import {
	addUnit,
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	isSameOrAfter,
	parseDateFromQuery,
	startOf,
	subtractUnit,
	utcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import { useEffect, useMemo } from "react"
import { useAuthUser } from "../Auth"
import {
	AsyncSelect,
	AsyncSelectProps,
	Badge,
	Box,
	Button,
	Container,
	Icons,
	Inline,
	RelativeTime,
	Select,
	SelectInput,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import { SelectField } from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { TWalletStats, WalletBalanceCard } from "../SubscriptionWalletCredits"
import { generatePath } from "../router-utils"

type TStatusOption =
	| "quote_created"
	| "only_itinerary_build"
	| "failed"
	| "pending"
type TIntervalType = "day" | "week" | "month"

const SelectStatus: Array<{ id: TStatusOption; name: string }> = [
	{ id: "quote_created", name: "Quote Created" },
	{ id: "only_itinerary_build", name: "Only Itinerary Build" },
	{ id: "failed", name: "Failed" },
	{ id: "pending", name: "Pending" },
]

type TUsageViewFilters = TSearchParams & {
	intervalType: TIntervalType
	after: Date
	before: Date
	status?: Array<{ id: TStatusOption; name: string }>
	tenant?: { id: number; name: string } | null
}
type TFiltersInQuery = TSearchParams & {
	intervalType: TIntervalType
	after: string
	before: string
	status?: string
	tenant?: string
}
function filtersToQuery(filters: TUsageViewFilters): TFiltersInQuery {
	const intervalType = filters.intervalType || "month"
	const query: TFiltersInQuery = {
		intervalType,
		after: dateToQuery(filters.after || startOf(new Date(), intervalType)),
		before: dateToQuery(filters.before || endOf(new Date(), intervalType)),
	}
	if (filters.q) query.q = filters.q
	if (filters.page) query.page = filters.page
	if (filters.status && filters.status.length > 0) {
		query.status = filters.status
			.map((item) => `${item.id}-${item.name}`)
			.join(",")
	}
	if (filters.tenant) {
		query.tenant = `${filters.tenant.id}#${filters.tenant.name}`
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TUsageViewFilters {
	const intervalType = query.intervalType || "month"
	const filters: TUsageViewFilters = {
		intervalType,
		after: query.after
			? startOf(parseDateFromQuery(query.after), "day")
			: startOf(new Date(), intervalType),
		before: query.before
			? endOf(parseDateFromQuery(query.before), "day")
			: endOf(new Date(), intervalType),
	}
	if (query.q) filters.q = query.q
	if (query.page) filters.page = query.page
	if (query.status) {
		const items = query.status.split(",")
		if (items.length > 0) {
			filters.status = items.map((t) => {
				const [id, ...names] = t.split("-")
				return {
					id: id as TStatusOption,
					name: names.join("-"),
				}
			})
		}
	}
	if (query.tenant) {
		const [idStr, ...rest] = query.tenant.split("#")
		const id = parseInt(idStr, 10)
		if (Number.isFinite(id)) {
			filters.tenant = { id, name: rest.join("#") }
		}
	}
	return filters
}

type TUsageStats = {
	data: {
		range: {
			after: string | null
			before: string | null
			interval_type: TIntervalType
		}
		avg_credits_per_generation?: number | null
		by_status: {
			only_itinerary_build: number
			quote_created: number
			pending: number
			failed: number
		}
		wallet?: (TWalletStats & { approx_remaining_parses?: number | null }) | null
		total_credits_consumed: number
	}
}

interface IGenerationHistory {
	id: number
	trip_id: number
	trip_quote_id: number | null
	destinations: string
	location_config: string
	status: TStatusOption
	credits_consumed: string
	error: string | null
	feedback_status: string | null
	feedback_reason: string | null
	feedback_at: string | null
	submitted_at: string
	streaming_started_at: string | null
	completed_at: string | null
	created_by: {
		id: number
		uid: string
		name: string
		email: string
	}
	tenant_id: number
	tenant_name: string
}

type StatusMeta = {
	title: string
	color: "success" | "danger" | "warning" | "primary"
}

const STATUS_MAP: Record<TStatusOption, StatusMeta> = {
	only_itinerary_build: {
		title: "Only Itinerary Build",
		color: "primary",
	},
	failed: {
		title: "Failed",
		color: "danger",
	},
	pending: {
		title: "Pending",
		color: "warning",
	},
	quote_created: {
		title: "Quote Created",
		color: "success",
	},
}

function getStatusMeta(status: TStatusOption): StatusMeta {
	return STATUS_MAP[status]
}

function Stat({ label, value }: { label: string; value: string }) {
	return (
		<Stack gap="2">
			<Text fontWeight="semibold">{label}</Text>
			<Text fontSize="xl" fontWeight="semibold">
				{value}
			</Text>
		</Stack>
	)
}

export function AIBuilderUsageView() {
	const { user } = useAuthUser()
	const adminMode = !user?.tenant_id

	const [query, setQuery] = useLocationQuery<
		TUsageViewFilters,
		TFiltersInQuery
	>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})

	const [params, setParams] = useSearch<TUsageViewFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const requestParams = useMemo(
		() => ({
			page: params.page || 1,
			q: params.q || undefined,
			after: dateToUTCString(params.after),
			before: dateToUTCString(params.before),
			status:
				params.status && params.status.length > 0
					? params.status.map((s) => s.id)
					: undefined,
			interval_type: params.intervalType,
			tenant_id: adminMode && params.tenant ? params.tenant.id : undefined,
		}),
		[params, adminMode]
	)

	return (
		<Search<TUsageViewFilters>
			title="AI Itinerary Builder Usage"
			initialParams={params}
			onSearch={setParams}
			placeholder="Search by trip id and created by"
			resetParams={(p) => ({
				q: "",
				page: 1,
				after: p.after,
				before: p.before,
				intervalType: p.intervalType,
				status: undefined,
			})}
			areAdvancedFiltersApplied={(params) => {
				const { after, before, intervalType, ...others } = params
				return areAdvancedFiltersAppliedDefault(others)
			}}
			Filters={Filters}
			actions={({ setSearchParams, searchParams }) => {
				return (
					<Inline alignItems="center" gap="2">
						<Button
							onClick={() => {
								const after = startOf(
									subtractUnit(
										searchParams.after,
										1,
										searchParams.intervalType
									),
									searchParams.intervalType
								)
								setSearchParams({
									after,
									before: endOf(after, searchParams.intervalType),
									page: 1,
								})
							}}
						>
							<Icons.ChevronDown rotate="90" />
						</Button>
						<Box>
							<SelectInput
								value={searchParams.intervalType}
								onChange={(e) => {
									const type = (e.currentTarget.value ||
										"month") as TIntervalType
									setSearchParams({
										intervalType: type,
										after: startOf(new Date(), type),
										before: endOf(new Date(), type),
										page: 1,
									})
								}}
							>
								<option value="month">Month</option>
								<option value="week">Week</option>
								<option value="day">Day</option>
							</SelectInput>
						</Box>
						<Button
							disabled={isSameOrAfter(
								startOf(searchParams.after, searchParams.intervalType),
								startOf(new Date(), searchParams.intervalType)
							)}
							onClick={() => {
								const after = startOf(
									addUnit(searchParams.after, 1, searchParams.intervalType),
									searchParams.intervalType
								)
								setSearchParams({
									after,
									before: endOf(after, searchParams.intervalType),
									page: 1,
								})
							}}
						>
							<Icons.ChevronDown rotate="270" />
						</Button>
					</Inline>
				)
			}}
		>
			<Container paddingY="4">
				<HeaderSection requestParams={requestParams} adminMode={adminMode} />
			</Container>
			<ListView<IGenerationHistory, TUsageViewFilters>
				params={params}
				pageKey={adminMode ? "admin-itinerary-builder" : "itinerary-builder"}
				fetch={(api) =>
					api
						.get("/itinerary-builder", {
							params: requestParams,
						})
						.then((resp) => resp.data)
				}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items }) => {
					return (
						<Table
							headers={[
								...(adminMode ? ["Tenant Name"] : ["Trip ID"]),
								"Destination",
								"Status",
								"Location Config",
								"Credits",
								"Processed",
							]}
							responsive
							bordered
							hover
							rows={items.map((item) => {
								const url = !adminMode
									? item.trip_quote_id
										? generatePath(`/trips/:tripId/quotes/:quoteId`, {
												tripId: item.trip_id.toString(),
												quoteId: item.trip_quote_id.toString(),
											})
										: generatePath(`/trips/:tripId`, {
												tripId: item.trip_id.toString(),
											})
									: generatePath(
											`/admin/tenants/:tenantId/subscription-wallet`,
											{ tenantId: item.tenant_id.toString() }
										)
								const statusMetaData = getStatusMeta(item.status)
								const badgeColor = statusMetaData.color as
									| "danger"
									| "success"
									| "warning"
									| "primary"
								return [
									<Link
										key="tenant_or_trip"
										to={url}
										anchored
										color="accent"
										fontWeight="semibold"
									>
										<Inline as="span" gap="1" alignItems="center">
											<Box>{adminMode ? item.tenant_name : item.trip_id}</Box>
											<Icons.ChevronDown rotate="270" size="3" opacity="50" />
										</Inline>
									</Link>,
									<Text key="destinations">{item.destinations}</Text>,
									<Stack key="status" gap="1" alignItems="start">
										<Badge status={badgeColor}>
											<Text textTransform="capitalize">
												{statusMetaData.title}
											</Text>
										</Badge>
									</Stack>,
									<Text color={"muted"} key="location_config">
										{item.location_config}
									</Text>,
									<Text
										key="credits"
										fontSize="sm"
										color={"muted"}
										title={`Wallet credits debited for this Itinerary build: ${item.credits_consumed}`}
									>
										{Number(item.credits_consumed).toFixed(3)}
									</Text>,
									item.completed_at ? (
										<Stack>
											<Text key="completed_at" fontSize="sm" color="muted">
												<RelativeTime timestamp={item.completed_at} />
											</Text>
											{item.created_by ? (
												<Text key="created_by" fontSize="sm" color="muted">
													by {item.created_by.name}
												</Text>
											) : null}
										</Stack>
									) : (
										<Text key="completed_at" fontSize="sm" color="muted">
											-
										</Text>
									),
								].filter(Boolean) as React.ReactNode[]
							})}
						/>
					)
				}}
			</ListView>
		</Search>
	)
}

function SelectAIBuilderTenants(props: Omit<AsyncSelectProps, "fetch">) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			cacheKey="itinerary-builder-tenants"
			fetch={(q) =>
				xhr
					.get("/itinerary-builder/tenants", {
						params: { q },
					})
					.then((resp) => resp.data.data)
			}
			{...props}
		/>
	)
}

function Filters() {
	const { user } = useAuthUser()
	const adminMode = !user?.tenant_id
	const usageStatusOptions = useMemo(() => {
		return adminMode
			? SelectStatus
			: SelectStatus.filter((s) => s.id !== "pending")
	}, [adminMode])
	return (
		<Stack gap="4">
			<SelectField
				select={Select}
				options={usageStatusOptions}
				name="status"
				label="Status"
				multiple
				fetchOnMount
			/>
			{adminMode ? (
				<SelectField
					select={SelectAIBuilderTenants}
					name="tenant"
					label="Tenant"
					multiple={false}
					placeholder="Tenant"
				/>
			) : null}
		</Stack>
	)
}

function HeaderSection({
	adminMode,
	requestParams,
}: {
	adminMode: boolean
	requestParams: {
		page: number
		q?: string
		after: string
		before: string
		status?: string[]
		interval_type: TIntervalType
		tenant_id?: number
	}
}) {
	const statsParams = useMemo(() => {
		const { page, ...rest } = requestParams
		return rest
	}, [requestParams])

	const xhr = useXHR()
	const { data: stats } = useSWR<TUsageStats>(
		[
			"-itinerary-builder-usage-stats",
			statsParams,
			adminMode ? "admin" : "tenant",
		],
		() =>
			xhr
				.get("/itinerary-builder/usage-stats", {
					params: statsParams,
				})
				.then((resp) => resp.data)
	)
	const walletCredits = stats?.data.wallet
	const totalItineraryBuilds = stats?.data.by_status.only_itinerary_build || 0
	const totalQuoteCreated = stats?.data.by_status.quote_created || 0
	const totalFailed = stats?.data.by_status.failed || 0
	const totalPending = stats?.data.by_status.pending || 0
	const totalGenerations = adminMode
		? totalItineraryBuilds + totalQuoteCreated + totalFailed + totalPending
		: totalItineraryBuilds + totalQuoteCreated + totalFailed
	const totalCreditsUsed = stats?.data?.total_credits_consumed?.toFixed(2)
	const averageCreditsPerGeneration =
		stats?.data.avg_credits_per_generation?.toFixed(2)

	const afterDate = utcTimestampToLocalDate(requestParams.after)
	const beforeDate = utcTimestampToLocalDate(requestParams.before)

	const rangeLabel =
		requestParams.interval_type === "month"
			? formatDate(afterDate, "MMM YYYY")
			: requestParams.interval_type === "day"
				? formatDate(afterDate, "DD MMM YYYY")
				: `${formatDate(afterDate, "DD MMM")} – ${formatDate(beforeDate, "DD MMM YYYY")}`

	return (
		<Stack gap="4" bgColor="default" padding="4" rounded="md" borderWidth="1">
			<Text fontWeight="semibold">{rangeLabel}</Text>
			<Inline gap="8" flexWrap="wrap" alignItems="start">
				<WalletBalanceCard
					wallet={walletCredits}
					adminTenantId={
						adminMode && requestParams.tenant_id
							? requestParams.tenant_id
							: undefined
					}
				/>
				<Stat
					label="Total"
					value={!totalGenerations ? "..." : String(totalGenerations)}
				/>
				<Stat
					label="Quote Created"
					value={!totalQuoteCreated ? "..." : String(totalQuoteCreated)}
				/>
				<Stat
					label="Only Itinerary Build"
					value={!totalItineraryBuilds ? "..." : String(totalItineraryBuilds)}
				/>
				{adminMode ? (
					<Stat
						label="Pending"
						value={!totalPending ? "..." : String(totalPending)}
					/>
				) : null}
				<Stat
					label="Failed"
					value={!totalFailed ? "..." : String(totalFailed)}
				/>
				<Stat
					label="Avg. AI"
					value={
						!averageCreditsPerGeneration
							? "..."
							: String(averageCreditsPerGeneration)
					}
				/>
				<Stat
					label="Credits Used"
					value={!totalCreditsUsed ? "..." : totalCreditsUsed.toString()}
				/>
			</Inline>
		</Stack>
	)
}
